import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import WishlistButton from './WishlistButton'
import { CONTACT_INFO } from '../constants/siteLinks'
import './ProductQuickView.css'

// Longer descriptions are clamped to 3 lines with a "Read more" toggle
const DESCRIPTION_CLAMP_CHARS = 150

const TRUST_POINTS = [
  { icon: 'fa-certificate', title: 'Genuine product', text: 'With warranty cover' },
  { icon: 'fa-truck-fast', title: 'Fast delivery', text: 'Across Rwanda' },
  { icon: 'fa-screwdriver-wrench', title: 'Expert installation', text: 'By our engineers' },
  { icon: 'fa-mobile-screen', title: 'Easy payment', text: 'MoMo, Airtel Money, card' },
]

const formatRwf = (value) => `RWF ${Math.round(value).toLocaleString()}`

/** Availability from stock; null when the product has no stock data. */
function getAvailability(product) {
  if (product.stock == null) return null
  const stock = Number(product.stock) || 0
  const threshold = Number(product.lowStockThreshold) || 10
  if (stock <= 0) return { tone: 'out', label: 'Out of stock', max: 0 }
  if (stock <= threshold) return { tone: 'low', label: `Only ${stock} left`, max: stock }
  return { tone: 'ok', label: 'In stock', max: stock }
}

/**
 * Product quick view dialog: zoomable image, price and savings, availability,
 * quantity, add to cart / wishlist, and a link to the full product page.
 */
function ProductQuickView({ product, onClose, onAddToCart }) {
  const [quantity, setQuantity] = useState(1)
  const [expanded, setExpanded] = useState(false)
  const [zoom, setZoom] = useState(null)
  const closeRef = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Focus the dialog on open, close on Escape, and return focus to the trigger on close.
  useEffect(() => {
    const previous = document.activeElement
    closeRef.current?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [])

  const availability = getAvailability(product)
  const soldOut = availability?.tone === 'out'
  const maxQty = availability ? Math.max(1, availability.max) : 99
  const savings =
    product.priceOldNum && product.priceOldNum > product.priceNewNum ? product.priceOldNum - product.priceNewNum : 0
  const description = product.description || ''
  const longDescription = description.length > DESCRIPTION_CLAMP_CHARS

  const onZoomMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    setZoom(`${x}% ${y}%`)
  }

  return (
    <div className="pqv" role="presentation" onClick={onClose}>
      <div
        className="pqv__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pqv-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button ref={closeRef} type="button" className="pqv__close" onClick={onClose} aria-label="Close quick view">
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>

        <div className="pqv__gallery">
          {product.discount && <span className="pqv__discount">{product.discount.replace('-', '')} OFF</span>}
          <div
            className={`pqv__stage${zoom ? ' is-zooming' : ''}`}
            onMouseMove={onZoomMove}
            onMouseLeave={() => setZoom(null)}
          >
            <img
              src={product.image}
              alt={product.title}
              style={zoom ? { transformOrigin: zoom } : undefined}
              onError={(e) => {
                e.currentTarget.src = '/placeholder-image.png'
              }}
            />
            <span className="pqv__zoom-hint" aria-hidden="true">
              <i className="fa-solid fa-magnifying-glass-plus" />
              Hover to zoom
            </span>
          </div>
        </div>

        <div className="pqv__info">
          {availability && (
            <span className={`pqv__stock is-${availability.tone}`}>
              <span className="pqv__dot" aria-hidden="true" />
              {availability.label}
            </span>
          )}

          <h2 id="pqv-title" className="pqv__title">
            {product.title}
          </h2>

          <div className="pqv__price">
            <strong>{product.priceNew}</strong>
            {product.priceOld && <del>{product.priceOld}</del>}
            {savings > 0 && <span className="pqv__save">You save {formatRwf(savings)}</span>}
          </div>

          {description && (
            <div className="pqv__desc">
              <p className={longDescription && !expanded ? 'is-clamped' : undefined}>{description}</p>
              {longDescription && (
                <button type="button" onClick={() => setExpanded((open) => !open)}>
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
          )}

          <div className="pqv__buy">
            <div className="pqv__qty" aria-label="Quantity">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1 || soldOut}
                aria-label="Decrease quantity"
              >
                <i className="fa-solid fa-minus" aria-hidden="true" />
              </button>
              <span aria-live="polite">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                disabled={quantity >= maxQty || soldOut}
                aria-label="Increase quantity"
              >
                <i className="fa-solid fa-plus" aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="pqv__add"
              disabled={soldOut}
              onClick={() => onAddToCart(product, quantity)}
            >
              <i className="fa-solid fa-cart-shopping" aria-hidden="true" />
              {soldOut ? 'Out of stock' : 'Add to Cart'}
            </button>
            <WishlistButton product={product} className="pqv__wish" />
          </div>

          <ul className="pqv__trust">
            {TRUST_POINTS.map((point) => (
              <li key={point.title}>
                <i className={`fa-solid ${point.icon}`} aria-hidden="true" />
                <span>
                  <strong>{point.title}</strong>
                  {point.text}
                </span>
              </li>
            ))}
          </ul>

          <div className="pqv__foot">
            <Link to={`/product-details/${product.id}`} className="pqv__details" onClick={onClose}>
              View full details
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </Link>
            <a href={CONTACT_INFO.phoneHref} className="pqv__help">
              <i className="fa-solid fa-headset" aria-hidden="true" />
              <span>
                Need advice? <strong>{CONTACT_INFO.phone}</strong>
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductQuickView
